import {
  Essence,
  EssenceCreateInput,
  EssenceListResponse,
  EssenceResponse,
  EssenceSelectInput,
  EssenceUpdateInput,
} from '@repo/types';
import { AxiosInstance } from 'axios';
import { axiosInstance } from '../axios-instance';
import { ENDPOINTS } from '../endpoint';
import { BaseApiService } from './base-api.service';

export class EssenceService extends BaseApiService {
  constructor(axios: AxiosInstance = axiosInstance) {
    // Since essence endpoints are nested under epaysage, we'll use the base axios instance
    super('', axios);
  }

  private getEndpoint(epaysageId: number) {
    return ENDPOINTS.ESSENCES.BY_EPAYSAGE(epaysageId.toString());
  }

  async createEssence(epaysageId: number, data: EssenceCreateInput): Promise<EssenceResponse> {
    return this.axios.post<EssenceResponse>(this.getEndpoint(epaysageId), data).then(response => response.data);
  }

  async getEssences(
    epaysageId: number,
    page = 1,
    limit = 10,
    search?: string,
    filters: {
      espece_id?: number;
      domaine?: string;
      status_travaux?: boolean;
    } = {}
  ): Promise<EssenceListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append('search', search);
    if (filters.espece_id) params.append('espece_id', filters.espece_id.toString());
    if (filters.domaine) params.append('domaine', filters.domaine);
    if (filters.status_travaux !== undefined) params.append('status_travaux', filters.status_travaux.toString());

    return this.axios.get<EssenceListResponse>(`${this.getEndpoint(epaysageId)}?${params.toString()}`).then(response => response.data);
  }

  async getEssence(epaysageId: number, id: number): Promise<EssenceResponse> {
    return this.axios.get<EssenceResponse>(ENDPOINTS.ESSENCES.BY_ID(epaysageId.toString(), id.toString())).then(response => response.data);
  }

  async updateEssence(epaysageId: number, id: number, data: EssenceUpdateInput): Promise<EssenceResponse> {
    return this.axios.put<EssenceResponse>(ENDPOINTS.ESSENCES.BY_ID(epaysageId.toString(), id.toString()), data).then(response => response.data);
  }

  async deleteEssence(epaysageId: number, id: number): Promise<EssenceResponse> {
    return this.axios.delete<EssenceResponse>(ENDPOINTS.ESSENCES.BY_ID(epaysageId.toString(), id.toString())).then(response => response.data);
  }

  async selectEssences(epaysageId: number, query: Partial<EssenceSelectInput>): Promise<{ data: Essence[] }> {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    return this.axios.get<{ data: Essence[] }>(`${ENDPOINTS.ESSENCES.SELECT(epaysageId.toString())}?${params.toString()}`).then(response => response.data);
  }

  async updateImage(
    epaysageId: number,
    id: number,
    field: 'img1' | 'img2' | 'img3',
    file: File
  ): Promise<EssenceResponse> {
    const formData = new FormData();
    formData.append('image', file);

    const endpoint = `${ENDPOINTS.ESSENCES.BY_ID(epaysageId.toString(), id.toString())}/update-image/${field}`;
    return this.axios
      .patch<EssenceResponse>(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => response.data);
  }

  async deleteImage(
    epaysageId: number,
    id: number,
    field: 'img1' | 'img2' | 'img3'
  ): Promise<EssenceResponse> {
    const endpoint = `${ENDPOINTS.ESSENCES.BY_ID(epaysageId.toString(), id.toString())}/delete-image/${field}`;
    return this.axios.delete<EssenceResponse>(endpoint).then((response) => response.data);
  }

  async updateStatusTravaux(ids: number[], status_travaux: boolean): Promise<void> {
    await this.axios.post(ENDPOINTS.ESSENCES.UPDATE_STATUS_TRAVAUX, { ids, status_travaux });
  }

  /** « Action réalisée » → Visite pour les espaces boisés des essences données. */
  async updateStatusVisite(ids: number[], status_visite: boolean): Promise<void> {
    await this.axios.post(ENDPOINTS.ESSENCES.UPDATE_STATUS_VISITE, { ids, status_visite });
  }

  async rotateImage(
    epaysageId: number,
    id: number,
    field: 'img1' | 'img2' | 'img3',
    rotation: number
  ): Promise<EssenceResponse> {
    const endpoint = `${ENDPOINTS.ESSENCES.BY_ID(epaysageId.toString(), id.toString())}/rotate-image/${field}`;
    return this.axios.patch<EssenceResponse>(endpoint, { rotation }).then((response) => response.data);
  }
}
